import { getNodeTypeConfigByPaletteKey } from '../core/config/node-types';
import type { NodeData } from '../types/flow-types';

export type FlowVisualRole = 'event' | 'condition' | 'action' | 'control' | 'delay' | 'state' | 'note';

export interface FlowCardDependency {
	label: string;
	icon: string;
	tone: 'neutral' | 'info' | 'warning';
}

export interface FlowCardVisualProfile {
	role: FlowVisualRole;
	roleLabel: string;
	roleIcon: string;
	isComposite: boolean;
	kindLabel: 'Standard card' | 'Special card' | 'Composite template';
	howItWorks: string;
	inputMeaning: string;
	outputMeaning: string;
	dependencies: FlowCardDependency[];
}

const roleMeta: Record<FlowVisualRole, { label: string; icon: string }> = {
	event: { label: 'Event', icon: 'fa-bolt' },
	condition: { label: 'Condition', icon: 'fa-circle-question' },
	action: { label: 'Action', icon: 'fa-play' },
	control: { label: 'Flow control', icon: 'fa-code-branch' },
	delay: { label: 'Delay', icon: 'fa-clock' },
	state: { label: 'State', icon: 'fa-memory' },
	note: { label: 'Note', icon: 'fa-note-sticky' },
};

const specialCards = new Set([
	'switchNode',
	'logicGate',
	'norGate',
	'hysteresisCondition',
	'actionSequence',
	'variableSwitch',
]);

const compositeCards = new Set(['hysteresisCondition', 'actionSequence', 'presenceCondition']);

function dependency(label: string, icon: string, tone: FlowCardDependency['tone']): FlowCardDependency {
	return { label, icon, tone };
}

function resolveRole(type: string, paletteKey: string): FlowVisualRole {
	if (type === 'note') return 'note';
	if (
		paletteKey === 'manualCondition' ||
		paletteKey === 'webhookTrigger' ||
		type === 'virtualComponentEvent'
	)
		return 'event';
	if (type === 'timer') return 'delay';
	if (type === 'setVariable' || type === 'variableCondition') return 'state';
	if (
		type === 'switchNode' ||
		type === 'variableSwitch' ||
		type === 'logicGate' ||
		type === 'actionSequence'
	)
		return 'control';
	if (
		type === 'action' ||
		type.endsWith('Action') ||
		type === 'notificationAction' ||
		type === 'virtualComponentAction'
	)
		return 'action';
	return 'condition';
}

function explanation(
	type: string,
	paletteKey: string,
): Pick<FlowCardVisualProfile, 'howItWorks' | 'inputMeaning' | 'outputMeaning'> {
	if (type === 'switchNode' || type === 'variableSwitch')
		return {
			howItWorks:
				'Reads one value and chooses exactly one matching case. If no case matches, it follows Default.',
			inputMeaning: 'The value to compare, received when the previous card runs.',
			outputMeaning: 'One named case, or Default when no case matches.',
		};
	if (type === 'logicGate')
		return {
			howItWorks:
				'Combines several incoming decisions. AND requires all inputs, OR requires any input, and NOR requires none.',
			inputMeaning: 'Two or more condition results.',
			outputMeaning: 'True or false after the selected logic rule is evaluated.',
		};
	if (type === 'hysteresisCondition')
		return {
			howItWorks:
				'Keeps its previous state inside a safe band: it turns on above the upper threshold and off below the lower threshold.',
			inputMeaning: 'A changing numeric device value.',
			outputMeaning:
				'True while active and false while inactive, without rapid toggling in between.',
		};
	if (type === 'actionSequence')
		return {
			howItWorks:
				'Runs its configured steps in order, including waits, notifications, and variable updates.',
			inputMeaning: 'A branch reaching this sequence.',
			outputMeaning: 'Continues only after every step has finished.',
		};
	if (type === 'timer')
		return {
			howItWorks: 'Pauses this branch for the configured duration, then continues once.',
			inputMeaning: 'A branch that should be delayed.',
			outputMeaning: 'Continue after the delay; there is no false result.',
		};
	if (paletteKey === 'webhookTrigger')
		return {
			howItWorks: 'Starts the flow when its external webhook endpoint is called.',
			inputMeaning: 'An external HTTP request; no canvas input is required.',
			outputMeaning: 'The flow starts once for each accepted request.',
		};
	if (paletteKey === 'manualCondition')
		return {
			howItWorks: 'Starts the flow only when a user explicitly runs it.',
			inputMeaning: 'A user pressing Run; no canvas input is required.',
			outputMeaning: 'Starts the connected branch.',
		};
	if (type === 'presenceCondition')
		return {
			howItWorks:
				'Translates a Presence sensor zone event into a clear enter, leave, present, or absent decision.',
			inputMeaning: 'Live occupancy data from the selected Presence zone.',
			outputMeaning: 'True when the chosen event matches; otherwise false.',
		};
	if (type === 'setVariable')
		return {
			howItWorks: 'Stores a value under a name so later cards can make decisions from it.',
			inputMeaning: 'A branch reaching this action.',
			outputMeaning: 'Continues after the variable has been updated.',
		};
	if (type === 'variableCondition')
		return {
			howItWorks: 'Compares a previously stored flow variable with the configured value.',
			inputMeaning: 'The current value of the named variable.',
			outputMeaning: 'True when it matches; otherwise false.',
		};
	if (type === 'note')
		return {
			howItWorks:
				'Adds an editor-only explanation or reminder. It is saved with the graph but never runs.',
			inputMeaning: 'No runtime input; a note may be placed near the cards it documents.',
			outputMeaning: 'No runtime output because notes are not executed.',
		};

	const role = resolveRole(type, paletteKey);
	if (role === 'event')
		return {
			howItWorks: 'Listens for the selected event and starts the connected branch when it occurs.',
			inputMeaning: 'The configured external or device event.',
			outputMeaning: 'Starts the branch when the event occurs.',
		};
	if (role === 'action')
		return {
			howItWorks: 'Performs the configured operation when this branch reaches the card.',
			inputMeaning: 'A branch reaching this action.',
			outputMeaning: 'Continues after the action is submitted.',
		};
	return {
		howItWorks: 'Evaluates the configured information and chooses the true or false path.',
		inputMeaning: 'The selected device, resource, provider, or current state.',
		outputMeaning: 'True when the condition matches; otherwise false.',
	};
}

function dependencies(type: string, paletteKey: string): FlowCardDependency[] {
	if (type === 'weatherCondition')
		return [
			dependency('Premium', 'fa-crown', 'warning'),
			dependency('Home location', 'fa-location-dot', 'info'),
			dependency('Weather provider', 'fa-cloud', 'neutral'),
		];
	if (type === 'sunriseSunsetCondition')
		return [
			dependency('Premium', 'fa-crown', 'warning'),
			dependency('Home location', 'fa-location-dot', 'info'),
		];
	if (type === 'energyCondition')
		return [dependency('Exchange-price provider', 'fa-chart-line', 'neutral')];
	if (type === 'presenceCondition') return [dependency('Presence sensor', 'fa-person-rays', 'info')];
	if (type === 'scriptAction') return [dependency('Gen2+ device', 'fa-microchip', 'info')];
	if (type.startsWith('virtualComponent') || paletteKey === 'emitVirtualButtonEvent')
		return [dependency('Existing virtual component', 'fa-cubes', 'info')];
	if (paletteKey === 'alexaNotification')
		return [dependency('Linked Alexa account', 'fa-volume-high', 'warning')];
	if (paletteKey === 'webhookTrigger') return [dependency('External caller', 'fa-globe', 'neutral')];
	return [];
}

export function getFlowCardVisualProfile(
	type: string,
	data: Partial<NodeData> = {},
): FlowCardVisualProfile {
	const paletteKey = String(data.paletteKey || type);
	const config = getNodeTypeConfigByPaletteKey(paletteKey);
	const role = resolveRole(type, paletteKey);
	const meta = roleMeta[role];
	const isComposite = compositeCards.has(paletteKey) || compositeCards.has(type);
	const isSpecial =
		config?.category === 'specialCards' || specialCards.has(paletteKey) || specialCards.has(type);
	return {
		role,
		roleLabel: meta.label,
		roleIcon: meta.icon,
		isComposite,
		kindLabel: isComposite ? 'Composite template' : isSpecial ? 'Special card' : 'Standard card',
		...explanation(type, paletteKey),
		dependencies: dependencies(type, paletteKey),
	};
}

export function getFlowPaletteVisualProfile(type: string, paletteKey?: string): FlowCardVisualProfile {
	return getFlowCardVisualProfile(type, { paletteKey: paletteKey || type } as Partial<NodeData>);
}

export function getFlowRoleSearchTerms(type: string, paletteKey?: string): string {
	const profile = getFlowPaletteVisualProfile(type, paletteKey);
	const config = getNodeTypeConfigByPaletteKey(paletteKey || type);
	return [
		profile.roleLabel,
		profile.kindLabel,
		profile.howItWorks,
		...profile.dependencies.map((item) => item.label),
		config?.category,
	]
		.join(' ')
		.toLowerCase();
}
